import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppTicks - delivery and read ticks on the owner's OWN sent messages (2026-10-14). HABITAT-FREE: the service
// (`Bridge/src/SentTicks.ts`, `LiveRun`) and the page (`AppTickFrame`) both read the wire through this file.
//
// ⭐ THE STATUS NUMBERS ARE rc14's `proto.WebMessageInfo.Status` (`WAProto/index.d.ts`): ERROR 0, PENDING 1,
// SERVER_ACK 2, DELIVERY_ACK 3, READ 4, PLAYED 5. rc14 emits a 1:1 receipt as `messages.update`
// `{key, update: {status}}` (`Socket/messages-recv.js:1276-1280`).
// ⭐ THE WIRE IS `{ "<message id>": <status> }` BOTH WAYS, ON A CHAT `/live` ASK: the page sends what it SHOWS for its
// own sent bubbles (0 = nothing yet), the service answers only ids it knows something BETTER about. An older page
// sends no `ticks`, so it is never answered early and never gets the field (`typing`'s rule).
// ⛔ NEVER "UNKNOWN": a bubble with no status shows NOTHING. Words: "Sent", "Delivered", "Read".

export class AppTicks {
    public static readonly ServerAck: number = 2;
    public static readonly Delivered: number = 3;
    public static readonly Read: number = 4;
    public static readonly Played: number = 5;
    /** How many ids one ask may carry (the newest own bubbles on the page). */
    public static readonly MaxIds: number = 200;
    /** The bubble attribute the page writes the shown status into. */
    public static readonly Attr: string = "data-wac-tick";

    /** A status this build shows (2..5), else 0. DOCUMENTED EXCEPTION: pure predicate. */
    public static StatusOf = (Value: unknown): number => {
        if (typeof Value !== "number" || Math.floor(Value) !== Value) { return 0; }
        if (Value < AppTicks.ServerAck || Value > AppTicks.Played) { return 0; }
        return Value;
    };

    /** The tooltip word for a shown status, "" for none. DOCUMENTED EXCEPTION: pure lookup. */
    public static WordOf = (Status: number): string => {
        if (Status >= AppTicks.Read) { return "Read"; }
        if (Status === AppTicks.Delivered) { return "Delivered"; }
        if (Status === AppTicks.ServerAck) { return "Sent"; }
        return "";
    };

    /** The icon kind: "one" (sent), "two" (delivered), "read" (two in the accent colour), "" none. Pure lookup. */
    public static KindOf = (Status: number): string => {
        if (Status >= AppTicks.Read) { return "read"; }
        if (Status === AppTicks.Delivered) { return "two"; }
        if (Status === AppTicks.ServerAck) { return "one"; }
        return "";
    };

    /**
     * Read a `ticks` object: id -> status (0 allowed on an ASK - "shows nothing yet"), at most `MaxIds`, ids by the
     * message-id allow-list. Null = not a ticks object (the field is then "not sent").
     */
    public static WireIn = (Raw: unknown, AllowZero: boolean): Record<string, number> | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Out: Record<string, number> = {};
            let Count: number = 0;
            for (const Id of Object.keys(Raw as Record<string, unknown>)) {
                if (Count >= AppTicks.MaxIds) { break; }
                if (/^[A-Za-z0-9]{8,64}$/.test(Id) === false) { continue; }
                const Value: unknown = (Raw as Record<string, unknown>)[Id];
                const S: number = AppTicks.StatusOf(Value);
                if (S === 0 && (AllowZero === false || Value !== 0)) { continue; }
                Out[Id] = S;
                Count = Count + 1;
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTicks.WireIn");
            return null;
        }
    };

    /** The ids where `Known` is better than `Shown` - what an answer carries. DOCUMENTED EXCEPTION: pure. */
    public static Better = (Shown: Record<string, number>, Known: Record<string, number>): Record<string, number> => {
        const Out: Record<string, number> = {};
        for (const Id of Object.keys(Shown)) {
            const K: number = AppTicks.StatusOf(Known[Id]);
            if (K > Shown[Id]) { Out[Id] = K; }
        }
        return Out;
    };
}
